import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { ICardInfo } from 'app/modules/my-limegear/components/standalone-components/interfaces/stanalone-components.data';
import { ITableColumns } from 'app/modules/reports/interfaces/reporting-data';
import { FinancialsCompSnapshotCharts } from 'app/modules/shared/classes/financials-comp-snapshot-chart-options';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import {
    ITableColumnProperties,
    ITableDataProperties,
} from 'app/modules/shared/components/common-kendo-table/common-kendo-table.component';
import { IChartTitleData, SmallCardData } from 'app/modules/shared/interfaces/card-data';
import {
    NoDataActionType,
    NoDataSourceType,
    NoDataTypes,
} from 'app/modules/shared/interfaces/no-data';
import * as Highcharts from 'highcharts';
import {
    CompensationPayPeriodData,
    CompSnapshotCardData,
    CSNetPayData,
    DrawBalanceData,
    EmployeeData,
    EmployeesData,
    HeldBalanceData,
    MarketingBalanceData,
    TransactionsData,
} from '../interfaces/incentives-data';
import { CompSnapshotService } from '../services/comp-snapshot.service';
import { ProfitabilityService } from '../services/profitability.service';
import {
    ICellClickEventInfo,
    ICustomTableInfoExtend,
} from 'app/modules/shared/components/custom-mat-table/custom-mat-table.component';
import { IChartParameters } from 'app/modules/shared/classes/sparkline-chart-options';
import { ReportingService } from 'app/modules/reports/services/reporting.service';

@Component({
    selector: 'app-financials-insight-comp-snapshot',
    templateUrl: './financials-insight-comp-snapshot.component.html',
    styleUrls: ['./financials-insight-comp-snapshot.component.scss'],
})
export class FinancialsInsightCompSnapshotComponent implements OnInit {
    payrollCalendarSettings: any;
    payrollPeriodSettings: any;
    payrollPeriods: CompensationPayPeriodData[];
    selectedCalendarItem: CompensationPayPeriodData[];
    smallCards: ICardInfo[];
    calendarTypes: CompensationPayPeriodData[] = [];
    employee: EmployeeData;
    selectedPayrollPeriod: CompensationPayPeriodData;
    cardParams: any;
    action: NoDataActionType = NoDataActionType.pageStart;
    netPayData: CSNetPayData;
    currentPayrollPeriodIndex: number;
    compSnapshotData: CompSnapshotCardData;
    compSnapshotDataOrder: string[] = ['netPay', 'draws', 'held', 'marketing'];
    drawBalanceData: DrawBalanceData;
    heldData: HeldBalanceData;
    marketingData: MarketingBalanceData;
    tableDataProperties: ITableDataProperties;
    tableColumnProperties: ITableColumnProperties;
    skip = 0;
    pageSize = 5;
    sort: SortDescriptor[] = [];
    sortable = {
        mode: 'single',
        allowUnsort: true,
    };
    scrollable = 'scrollable';
    type: 'numeric' | 'input' = 'input';
    pageable = {
        refresh: true,
        info: true,
        type: this.type,
        previousNext: true,
        pageSizes: [5, 10, 20],
    };
    noDataForCompany: boolean;
    noDataType: NoDataTypes;
    loading: boolean;
    totalLoans: number;
    totalLoanVolume: number;
    totalNetPayVolume: number;
    isDataReady: boolean;
    hasData: boolean;
    waterfallChart: any;
    employees: EmployeesData[] = [];
    employeeListSettings: any;
    branchListSettings: any;
    selectedEmployee: any;
    selectedBranch: any;
    selectedRegion: any;
    hasNoEmpPayData: boolean = false;
    otherEmployeeClicked: boolean = false;
    selectedEmployeeID: string = '';
    branches: any[] = [];
    regions: any[] = [];
    regionListSettings: any;
    hasBranches: boolean = false;
    hasRegions: boolean = false;
    allBranches: any[];
    allRegions: any[];
    allEmployees: any[];
    columnInfoList: ITableColumns[];
    customTableInfo: ICustomTableInfoExtend;
    searchTerm: string = '';

    constructor(
        private compSnapshotService: CompSnapshotService,
        private reusableFunctions: ReusableFunctions,
        private profitabilityService: ProfitabilityService,
        private router: Router,
        private reportService: ReportingService
    ) {}

    ngOnInit(): void {
        this.getEmployeeAndPayPeriodData();
        this.payrollCalendarSettings = {
            singleSelection: true,
            text: 'Select Calendar',
            enableSearchFilter: false,
            showCheckbox: false,
            labelKey: 'calendarName',
            primaryKey: 'payrollID',
            enableFilterSelectAll: false,
        };

        this.employeeListSettings = {
            singleSelection: true,
            text: 'Select Employee',
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: 'lfName',
            primaryKey: 'empID',
            enableFilterSelectAll: false,
        };

        this.branchListSettings = {
            singleSelection: true,
            text: 'Select Branch',
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: 'branchName',
            primaryKey: 'brID',
            enableFilterSelectAll: false,
        };

        this.regionListSettings = {
            singleSelection: true,
            text: 'Select Region',
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: 'regionName',
            primaryKey: 'regID',
            enableFilterSelectAll: false,
        };

        this.initializeSmallCards();
        this.initiateTableProperties();

        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: 'Net Pay Table Summary',
            inputSource: NoDataSourceType.table,
            noDataCustomMessage: 'No Results found for Net Pay.',
        };
        this.getEmptyLargeCardData();
    }

    initializeSmallCards() {
        this.smallCards = [
            {
                index: 0,
                title: 'NET PAY',
                description: 'Net Pay',
            },
            {
                index: 1,
                title: 'DRAW BALANCE',
                description: 'Draw Balance',
            },
            {
                index: 2,
                title: 'HELD BALANCE',
                description: 'Held Balance',
            },
            {
                index: 3,
                title: 'MARKETING BALANCE',
                description: 'Marketing Balance',
            },
        ];

        this.smallCards.forEach((card) => {
            card.card = Object.assign({}, this.getEmptySmallCardData(card));
        });
    }

    setNoDataType(action: NoDataActionType, customMessage: string = '') {
        this.noDataForCompany = true;
    }

    getEmployeeAndPayPeriodData() {
        this.compSnapshotService.getEmployeeData().subscribe((data) => {
            this.employee = data;
            if (data) {
                const params = { email: data.loginEmail };
                this.compSnapshotService.getPayPeriodData(params).subscribe((data) => {
                    this.payrollPeriods = data;
                    this.selectedCalendarItem = data.filter((x) => x.isCurrent);
                    this.selectedPayrollPeriod = this.payrollPeriods.find((x) => x.isCurrent);
                    this.currentPayrollPeriodIndex = data.findIndex((x) => x.isCurrent);
                    this.getCalendarTypes();
                    this.getCardParams();
                    this.getCardData();
                });
            } else {
                this.hasNoEmpPayData = true;
                this.getCardData();
            }
        });
    }

    getCardData() {
        this.initializeSmallCards();
        if (this.hasNoEmpPayData) {
            this.prepareNoDataSmallCards();
            this.prepareNoDataLargeCards();
            this.customTableInfo = this.reportService.refreshCustomMatTable(
                this.customTableInfo,
                false,
                true,
                this.action,
                this.searchTerm
            );
        } else {
            this.customTableInfo = this.resetTableData();
            this.compSnapshotService
                .getCompSnapshotData(
                    this.cardParams,
                    this.otherEmployeeClicked,
                    this.selectedEmployeeID
                )
                .subscribe(
                    (data) => {
                        this.branches = this.branches?.length ? this.branches : data.branches;
                        this.allBranches = data.branches;
                        this.regions = this.regions?.length ? this.regions : data.regions;
                        this.allRegions = data.regions;
                        this.employees = this.employees.length ? this.employees : data.employees;
                        this.allEmployees = data.employees;
                        this.hasBranches = data.branches?.length > 0;
                        this.hasRegions = data.regions?.length > 0;
                        this.addDefaultAlls();
                        this.employees = this.sortEntities(this.employees, 'lfName');
                        this.branches = this.sortEntities(this.branches, 'branchName');
                        this.regions = this.sortEntities(this.regions, 'regionName');
                        // Has payroll/employee data but not comp snapshot data for this period.
                        if (data.netPay.payrollDetails.length < 1) {
                            const noCompDataVerbiage = 'No Comp Data This Period';
                            this.prepareNoDataSmallCards(noCompDataVerbiage);
                            this.prepareNoDataLargeCards(noCompDataVerbiage);
                            if (data.employees.length && !this.otherEmployeeClicked) {
                                this.employees = this.employees.length
                                    ? this.sortEntities(this.employees, 'lfName')
                                    : this.employees;
                                this.selectedBranch = [
                                    this.branches?.find((x) => x.branchName === 'All'),
                                ];
                                this.selectedRegion = [
                                    this.regions?.find((x) => x.regionName === 'All'),
                                ];
                                this.selectedEmployee = [
                                    this.employees.find((x) => x.lfName === this.employee.lfName),
                                ];
                            }
                            this.customTableInfo = this.reportService.refreshCustomMatTable(
                                this.customTableInfo,
                                false,
                                true,
                                this.action,
                                this.searchTerm
                            );
                        } else {
                            const {
                                netPay,
                                draws,
                                marketing,
                                held,
                                transactions,
                                employees,
                                branches,
                                regions,
                            } = data;

                            if (held === null) {
                                this.smallCards = this.smallCards.filter((x) => x.index !== 2);
                            }

                            if (marketing === null) {
                                this.smallCards = this.smallCards.filter((x) => x.index !== 3);
                            }
                            this.compSnapshotData = {
                                netPay,
                                draws,
                                marketing,
                                held,
                                transactions,
                                employees,
                                branches,
                                regions,
                            };
                            this.netPayData = this.compSnapshotData.netPay;
                            this.drawBalanceData = draws.draws;
                            this.heldData = held?.summary;
                            this.marketingData = marketing;
                            if (!this.otherEmployeeClicked) {
                                this.employees = employees.sort((a, b) =>
                                    a.lfName.localeCompare(b.lfName)
                                );
                                this.selectedEmployee = [
                                    this.employees.find((x) => x.lfName === this.employee.lfName),
                                ];
                            }
                            const borrowerLoans = this.netPayData.payrollDetails.filter(
                                (x) => x.borrower
                            );
                            this.totalLoans = borrowerLoans.length;
                            this.totalLoanVolume = borrowerLoans.reduce(
                                (a, { loanAmt }) => a + loanAmt,
                                0
                            );

                            this.customTableInfo.dataSet = this.getNonBorrowerData(
                                this.netPayData.payrollDetails
                            );
                            this.customTableInfo = this.reportService.refreshCustomMatTable(
                                this.customTableInfo,
                                true,
                                true,
                                this.action,
                                this.searchTerm
                            );

                            this.prepareSmallCards();
                            this.setMainChartData(transactions);
                        }
                    },
                    (error) => {
                        this.customTableInfo = this.reportService.refreshCustomMatTable(
                            this.customTableInfo,
                            false,
                            true,
                            this.action,
                            this.searchTerm
                        );
                    }
                );
        }
    }

    getNonBorrowerData(payrollDetails) {
        for (const detail of payrollDetails) {
            if (detail.borrower === null) {
                detail.borrower = detail.description;
                detail.loanAmt = '';
                detail.payType = detail.adjustmentType;
            } else {
                detail.payType = 'Commission';
            }
        }
        return payrollDetails;
    }

    getCardParams() {
        const selectedPayPeriodIndex = this.payrollPeriods.findIndex(
            (x) => x.payrollID === this.selectedPayrollPeriod.payrollID
        );
        const selectedPayPeriod = this.payrollPeriods[selectedPayPeriodIndex + 1];
        this.cardParams = {
            payStartDate: selectedPayPeriod.loanDateStart,
            payStopDate: selectedPayPeriod.loanDateStop,
            employeeEmail: this.employee.loginEmail,
        };
    }

    onCalendarSelect(event) {
        this.selectedCalendarItem = [event];
        this.payrollPeriods = this.payrollPeriods.filter(
            (x) => x.calendarName === this.selectedCalendarItem[0].calendarName
        );
    }

    onPayPeriodSelect(event) {
        this.selectedPayrollPeriod = event;
        this.getCardParams();
        this.getCardData();
    }

    onEmployeeSelect(event) {
        this.otherEmployeeClicked = true;
        this.selectedEmployee = [event];
        this.selectedEmployeeID = event.empID;
        this.getCardParams();
        this.getCardData();
    }

    onBranchSelect(event) {
        const branchIDs = this.branches.map((x) => x.brID);
        if (event.brID === 0) {
            this.employees = this.allEmployees.filter((x) => branchIDs.includes(x.brID));
            return;
        }
        this.selectedBranch = [event];
        this.employees = this.allEmployees.filter((x) => x.brID === event.brID);
    }

    onRegionSelect(event) {
        if (event.regID === 0) {
            const branchIDs = this.allBranches.map((x) => x.brID);
            // Using structuredClone is the newer, and probably better choice. Refactor this when available after Angular & @types/node versions are 17 or higher.
            this.branches = JSON.parse(JSON.stringify(this.allBranches));
            this.selectedBranch = [this.allBranches.find((x) => x.brID === 0)];
            this.employees = this.allEmployees.filter((x) => branchIDs.includes(x.brID));
            return;
        }
        this.selectedRegion = [event];
        this.branches = this.sortEntities(
            this.allBranches.filter((x) => x.regID === event.regID || x.brID === 0),
            'branchName'
        );
        const branchIDs = this.branches.map((x) => x.brID);
        this.employees = this.allEmployees.filter((x) => branchIDs.includes(x.brID));
        const isCurrentSelectedInBranches = this.branches.find(
            (x) => x.brID === this.selectedBranch[0].brID
        );

        if (!isCurrentSelectedInBranches) {
            this.selectedBranch = [this.branches.find((x) => x.brID === 0)];
        }
    }

    clickPreviousPeriod() {
        this.currentPayrollPeriodIndex--;
        this.selectedPayrollPeriod = this.payrollPeriods[this.currentPayrollPeriodIndex];
        this.getCardParams();
        this.getCardData();
    }

    clickNextPeriod() {
        this.currentPayrollPeriodIndex++;
        this.selectedPayrollPeriod = this.payrollPeriods[this.currentPayrollPeriodIndex];
        this.getCardParams();
        this.getCardData();
    }

    getEmptySmallCardData(
        cardInf: any,
        hasData = false,
        isDataReady = false,
        action = null
    ): SmallCardData {
        return {
            title: cardInf.title,
            mainValue: '',
            unit: '',
            faIcon: '',
            faIconColor: '',
            comparisonValue: '',
            urlSegment: cardInf.drillDUrlSegment,
            routerLink: '',
            svgData: '',
            isDataReady: isDataReady,
            hasData: hasData,
            searchTerm: '',
            action,
            noDataSource: NoDataSourceType.verySmallDataCard,
        };
    }

    getCalendarTypes() {
        const types = [];
        const calendarTypes = [];
        for (let period of this.payrollPeriods) {
            if (!types.includes(period.calendarName)) {
                types.push(period.calendarName);
                calendarTypes.push(period);
            }
        }

        this.calendarTypes = calendarTypes;
    }

    prepareNoDataSmallCards(customMessage: string = '') {
        for (let i = 0; i < this.smallCards.length; i++) {
            let smallCard = this.smallCards[i];
            smallCard.card.hasData = false;
            smallCard.card.isDataReady = true;
            if (customMessage) smallCard.card.noDataTitleCustomMessage = customMessage;
            this.smallCards[i] = Object.assign({}, smallCard);
        }
    }

    prepareNoDataLargeCards(customMessage: string = '') {
        if (customMessage) this.noDataType.noDataTitleCustomMessage = customMessage;
        this.totalNetPayVolume = 0;
        this.totalLoanVolume = 0;
        this.totalLoans = 0;

        this.noDataForCompany = true;
        if (customMessage) {
            this.updateLargeCard(this.waterfallChart, null, false, customMessage);
        } else {
            this.updateLargeCard(this.waterfallChart, null, false);
        }
    }

    prepareSmallCards() {
        let noData;
        let numberToFormat;

        for (let i = 0; i < this.smallCards.length; i++) {
            switch (this.smallCards[i].index) {
                case 0:
                    numberToFormat = this.netPayData.payrollSummary.amount;
                    break;
                case 1:
                    numberToFormat = this.drawBalanceData.totalBalance;
                    break;
                case 2:
                    numberToFormat = this.heldData?.totalBalance ?? 0;
                    break;
                case 3:
                    numberToFormat = this.marketingData?.currentBalance.amount ?? 0;
                    break;
                default:
            }

            let smallCard = this.smallCards[i];

            if (noData) {
                smallCard.card = Object.assign(
                    {},
                    this.getEmptySmallCardData(smallCard, false, true, this.action)
                );
            } else {
                smallCard.card.routerLink = '';
                smallCard.card.urlSegment = '';
                smallCard.card.hasData = true;
                smallCard.card.isDataReady = true;
                smallCard.card.mainValue = this.reusableFunctions.formatNumber(
                    numberToFormat,
                    2,
                    'currency'
                );
            }
            this.smallCards[i] = Object.assign({}, smallCard);
        }
    }

    prepareWaterfallData(data: TransactionsData[]) {
        const waterfallOrder = [
            'Commission',
            'Bonus',
            'Marketing',
            'Earnings Recapture',
            'Override',
        ];
        const sortByObject = waterfallOrder.reduce((obj, item, index) => {
            return {
                ...obj,
                [item]: index,
            };
        }, {});
        const aggregateDataByClosingMethod = data.reduce((acc, curr) => {
            if (!acc[curr.payType]) {
                acc[curr.payType] = {
                    name: curr.payType,
                    y: curr.tranAmount,
                    color: waterfallOrder.indexOf(curr.payType) > 2 ? '#EA4A22' : '#57D786',
                };
            } else {
                acc[curr.payType].y += curr.tranAmount;
            }
            return acc;
        }, {});

        const waterfallData: any = Object.values(aggregateDataByClosingMethod);
        waterfallData.sort((a, b) => sortByObject[a.name] - sortByObject[b.name]);
        const netPayAmt = waterfallData.reduce((a, { y }) => a + y, 0);
        waterfallData.push({ name: 'Net Pay', y: netPayAmt, isSum: true, color: '#2978A8' });

        return waterfallData;
    }

    clickNetPay(loanNumber: string) {
        const param = {
            fromDatePeriod: 'Trailing 3m',
            loanNumber,
            isLoanProfitabilityDialog: true,
        };
        this.profitabilityService.setLoanPrfitabilityDialogParams(param, false);
        this.router.navigate([{ outlets: { modal: 'loanDetails' + '/' + loanNumber } }]);
    }

    getEmptyLargeCardData() {
        this.waterfallChart = {
            name: 'WaterfallChart',
        };

        let chartTitleData: IChartTitleData = {
            showChartCustomTitle: false,
            chartTile: '',
            chartLabelColor: '',
            chartComparisonValue: '',
            chartMainValue: null,
            chartSecondaryValue: '',
            isMainValueUnitVisible: false,
            showMainValue: false,
            chartSubItemValue: '',
            isChartSubValueVisible: false,
            svgIconString: '',
        };

        this.waterfallChart.largeCardInteriorOptions = {
            action: this.action,
            componentTitle: 'Net Pay',
            searchTerm: '',
            hasData: false,
            isDataReady: false,
            showTitle: true,
            accessToUrl: '',
            uniqueId: `waterfall-chart`,
            chartTitleData: Object.assign({}, chartTitleData),
        };
    }

    setMainChartData(data): void {
        const chartData = this.prepareWaterfallData(data);
        const compSnapshotChartOptions = new FinancialsCompSnapshotCharts(
            this.router,
            this.reusableFunctions
        );
        let chartOptions = compSnapshotChartOptions.createWaterfallChart(chartData);
        this.updateLargeCard(this.waterfallChart, chartOptions, true);
    }

    updateLargeCard(
        cardInfo,
        chartOptions: Highcharts.Options,
        hasData: boolean,
        customMessage: string = ''
    ) {
        if (cardInfo && chartOptions) {
            cardInfo.largeCardInteriorOptions.chart = new Highcharts.Chart(
                cardInfo.largeCardInteriorOptions.uniqueId,
                chartOptions
            );
        }

        cardInfo.largeCardInteriorOptions.hasData = hasData;
        cardInfo.largeCardInteriorOptions.isDataReady = true;
        if (customMessage)
            cardInfo.largeCardInteriorOptions.noDataTitleCustomMessage = customMessage;
        cardInfo.largeCardInteriorOptions = Object.assign({}, cardInfo.largeCardInteriorOptions);
    }

    addDefaultAlls() {
        const defaultRegion = { regionName: 'All', regID: 0 };
        if (this.allRegions?.length && !this.allRegions?.find((x) => x.regionName === 'All')) {
            this.allRegions.push(defaultRegion);
        }

        if (this.regions?.length && !this.regions.find((x) => x.regionName === 'All')) {
            this.regions.push(defaultRegion);
        }

        const defaultBranch = { branchName: 'All', brID: 0 };
        if (this.allBranches?.length && !this.allBranches.find((x) => x.branchName === 'All')) {
            this.allBranches.push(defaultBranch);
        }

        if (this.branches?.length && !this.branches.find((x) => x.branchName === 'All')) {
            this.branches.push(defaultBranch);
        }
    }

    sortEntities(entity, sortedColumn) {
        let sortedEntity;
        if (entity) {
            sortedEntity = entity.sort((a, b) =>
                a[sortedColumn] === 'All'
                    ? -1
                    : b[sortedColumn] === 'All'
                    ? 1
                    : a[sortedColumn].localeCompare(b[sortedColumn])
            );
        }
        return sortedEntity;
    }

    initiateTableProperties() {
        this.columnInfoList = [
            {
                fieldId: 'payType',
                fieldName: 'Pay Type',
                fieldOrder: 0,
                isSortable: true,
                defaultSorting: 'asc',
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'string',
                width: 170,
                filterable: false,
                filter: 'text',
            },
            {
                fieldId: 'loanNumber',
                fieldName: 'Loan #',
                backUpFieldId: 'hierarchyField',
                isHyperlink: true,
                cellClickEventType: 'hyperlink',
                aggregationType: 'countNoNull',
                fieldOrder: 1,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'string',
                width: 160,
                filterable: false,
                filter: 'text',
            },
            {
                fieldId: 'loanAmt',
                fieldName: 'Loan Amount',
                fieldOrder: 2,
                isSortable: false,
                headerAlignment: 'left',
                dataAlignment: 'left',
                aggregationType: 'sum',
                dataType: 'currency',
                decimalPlaces: 0,
                width: 115,
                filterable: false,
                filter: 'numeric',
                isDynamicColumn: true,
            },
            {
                fieldId: 'bps',
                fieldName: 'BPS',
                fieldOrder: 3,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'number',
                decimalPlaces: 0,
                width: 70,
                filterable: false,
                filter: 'numeric',
                isDynamicColumn: true,
            },
            {
                fieldId: 'amount',
                fieldName: 'Pay',
                fieldOrder: 4,
                isSortable: true,
                headerAlignment: 'right',
                dataAlignment: 'right',
                dataType: 'currency',
                aggregationType: 'sum',
                decimalPlaces: 2,
                width: 115,
                filterable: false,
                filter: 'numeric',
                isDynamicColumn: true,
            },
        ];

        this.customTableInfo = {
            tableId: 'performance-insights-kpi-sparkline-table',
            tableHeight: 360,
            hasData: false,
            isDataReady: false,
            columnInfoList: this.columnInfoList,
            dataSet: [],
            searchTerm: '',
            action: NoDataActionType.pageStart,
            title: '',
            bandColorList: [],
            dataHandleInUiSide: true,
            showSummary: true,
        };
    }

    resetTableData() {
        let customTableInfo = Object.assign({}, this.customTableInfo);
        customTableInfo.dataSet = [];
        return this.reportService.refreshCustomMatTable(
            customTableInfo,
            false,
            false,
            this.action,
            this.searchTerm
        );
    }

    cellClickedEvent(event: ICellClickEventInfo) {
        const cellData: any = event.cellData;
        const loanNumber = cellData.loanNumber;
        if (event.columnInfo.fieldId === 'loanNumber') {
            const param = {
                fromDatePeriod: 'Trailing 3m',
                loanNumber,
                isLoanProfitabilityDialog: true,
            };
            this.profitabilityService.setLoanPrfitabilityDialogParams(param, false);
            this.router.navigate([{ outlets: { modal: 'loanDetails' + '/' + loanNumber } }]);
        }
    }
}
